/**
 * "Most in demand" ordering for the Career Explorer.
 *
 * Display-only. Nothing here scores, matches, or feeds the engine — it exists
 * so a learner can ask "which of these is the market actually asking for?"
 * and get an honest answer.
 *
 * Precedence is the same rule the rest of the app follows, in the same order:
 *
 *   live market table (Supabase)  →  bundled fallback  →  sealed workbook CSV  →  neutral
 *
 * A career with no evidence is **neutral (50) and flagged**, never zero. Zero
 * would push 3,000 unpriced roles to the bottom of the list and read as
 * "nobody wants this", which is a claim we have no evidence for.
 *
 * Ordering: demand band first, projected growth as the tie-break, then title
 * and ci_id so the same list comes back in the same order every time.
 */
import { loadCareersBundle } from "@/data/careersBundle";
import { loadMarketIntelligence } from "@/lib/marketIntelligence";
import { loadCareerSheet, careerSlug } from "@/lib/careerSheet";
import { demandLevel } from "@/lib/careerIntel";

/** Neutral score for a career we have no demand evidence for. */
export const NEUTRAL_DEMAND = 50;

export interface DemandRank {
  /** 0..100. Band level where known, `NEUTRAL_DEMAND` where not. */
  score: number;
  /** The sealed workbook's 2026 band level, used to break band ties. */
  nearTerm: number | null;
  /** Projected growth %, used only as a tie-break. */
  growthPct: number | null;
  /** The band we're showing, in the source's own words. */
  band: string | null;
  /** Where the band came from, so the learner can audit the ranking. */
  source: "live" | "fallback" | "workbook" | null;
  /** False when the score is the neutral placeholder. */
  known: boolean;
}

export interface DemandIndex {
  size: number;
  get(ciId: string): DemandRank;
}

const NEUTRAL: DemandRank = {
  score: NEUTRAL_DEMAND, nearTerm: null, growthPct: null, band: null, source: null,
  known: false,
};

let cache: Promise<DemandIndex> | null = null;

/**
 * Build the whole-corpus demand index once, from bulk reads only. Never one
 * request per career — 5,877 round trips is not a loading state, it's an
 * outage.
 */
export function loadDemandIndex(): Promise<DemandIndex> {
  if (!cache) cache = build();
  return cache;
}

export function clearDemandIndexCache() {
  cache = null;
}

async function build(): Promise<DemandIndex> {
  const map = new Map<string, DemandRank>();
  try {
    const [bundle, market, sheet] = await Promise.all([
      loadCareersBundle(),
      loadMarketIntelligence(),
      loadCareerSheet().catch(() => new Map()),
    ]);

    for (const row of bundle.rows) {
      const live = market.get(row.ciId);
      const fallback = bundle.fallbackMarket[row.rowIndex];
      const csv = sheet.get(careerSlug(row.name)) ?? null;

      const liveBand = live?.demand2040 ?? null;
      const fallbackBand = fallback ? fallback[2] : null;
      const band = liveBand ?? fallbackBand ?? csv?.demand2026 ?? null;
      const source = liveBand
        ? ("live" as const)
        : fallbackBand
          ? ("fallback" as const)
          : csv?.demand2026
            ? ("workbook" as const)
            : null;

      const level = demandLevel(band);
      map.set(row.ciId, {
        score: level ?? NEUTRAL_DEMAND,
        nearTerm: demandLevel(csv?.demand2026),
        growthPct: csv?.growthPct ?? null,
        band,
        source: level != null ? source : null,
        known: level != null,
      });
    }
  } catch {
    // An empty index means the caller falls back to alphabetical order,
    // which is wrong-but-harmless. A thrown error would blank the page.
  }
  return { size: map.size, get: (id) => map.get(id) ?? NEUTRAL };
}

/**
 * Deterministic comparator: demand desc, growth desc, title A–Z, ci_id A–Z.
 * Codepoint comparison on the tie-breaks so two devices agree.
 */
export function compareByDemand(
  a: { ciId: string; name: string },
  b: { ciId: string; name: string },
  index: DemandIndex,
): number {
  const ra = index.get(a.ciId);
  const rb = index.get(b.ciId);
  if (rb.score !== ra.score) return rb.score - ra.score;
  // Whole bands are coarse — thousands of roles share "Very High". The
  // workbook's near-term band and projected growth separate them, so the top
  // of the list is not just the alphabet wearing a demand label.
  const na = ra.nearTerm ?? -1;
  const nb = rb.nearTerm ?? -1;
  if (nb !== na) return nb - na;
  const ga = ra.growthPct ?? -1;
  const gb = rb.growthPct ?? -1;
  if (gb !== ga) return gb - ga;
  if (a.name !== b.name) return a.name < b.name ? -1 : 1;
  return a.ciId < b.ciId ? -1 : a.ciId > b.ciId ? 1 : 0;
}
